import {
  defineStore,
  storeToRefs,
} from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { accountApiConfig } from '@/config/api/account';
import { useClientApi } from '@/plugins/api/lk';
import { decorateApiActions } from '@/shared/lib';
import type { Agent } from '@/types/Agent';
import type { PageInfo } from '@/types/PageInfo';

import { useAuthStore } from '../authStore';

import type {
  AgentResponse,
  AgentsOrdering,
  AgentsRequestParams,
} from './types';

export type AgentsList = Array<Agent>;
export type PagedAgents = Array<AgentsList>;

export const ORDERING_AGENTS_SLUGS = {
  nameAsc: 'name',
  firedAsc: 'agent_fire_status__date_task',
  nameDes: '-name',
  firedDes: '-agent_fire_status__date_task',
};
const orderingDateCreate = [
  {
    label: 'По убыванию имени',
    value: ORDERING_AGENTS_SLUGS.nameDes,
  },
  {
    label: 'По возрастанию имени',
    value: ORDERING_AGENTS_SLUGS.nameAsc,
  },
  {
    label: 'По убыванию запроса',
    value: ORDERING_AGENTS_SLUGS.firedDes,
  },
  {
    label: 'По возрастанию запроса',
    value: ORDERING_AGENTS_SLUGS.firedAsc,
  },
];

export const useAgentsStore = defineStore('agents', () => {
  const authStore = useAuthStore();
  const { strategy } = storeToRefs(authStore);
  const pagedAgents = ref<PagedAgents>([]);
  const agents = computed(() => pagedAgents.value.flat(1));
  const agentDetail = ref<Agent | undefined>();

  const count = ref(0);
  const pageInfo = ref<PageInfo | null>(null);
  const ordering = ref<AgentsOrdering | null>(null);
  const search = ref<string | null>(null);

  async function getAgents (params: AgentsRequestParams, append = false) {
    const { dependentRole } = accountApiConfig;

    if (!strategy.value) {
      throw new Error('auth strategy info not received');
    }
    const url = dependentRole.list('agents', strategy.value);
    const response = await useClientApi().$get<AgentResponse>(url, { params });

    if (!append) {
      pagedAgents.value.splice(0);
    }

    if (response.result.length) {
      pagedAgents.value.push(response.result);
    }

    count.value = response.count;
    pageInfo.value = response.page_info;

    return response;
  }

  async function getAgent (id: number) {
    if (!strategy.value) {
      throw new Error('auth role info not received');
    }

    const { dependentRole } = accountApiConfig;
    const url = dependentRole.detail('agents', strategy.value, id);

    return agentDetail.value = await useClientApi().$get<Agent>(url);
  }

  async function getSpecs () {
    ordering.value = orderingDateCreate;
  }

  function clearAgent () {
    agentDetail.value = undefined;
  }

  const decoratedApiActions = decorateApiActions({
    getAgents,
    getSpecs,
    getAgent,
  }, 'agents/agents.ts');

  const decoratedApiActionsRethrow = decorateApiActions({
    getAgentsRethrow: getAgents,
    getAgentRethtow: getAgent,
  }, 'agents/agents.ts', true);

  return {
    agents,
    pagedAgents,
    count,
    pageInfo,
    ordering,
    search,
    agentDetail,
    ...decoratedApiActions,
    ...decoratedApiActionsRethrow,
    clearAgent,
  };
});
